import { computed, type ComputedRef } from 'vue';
import { useRoute, type RouteLocationNormalized } from 'vue-router';

import type { PermsKey } from '@/platform/identity/perms';

// Named layouts for the host chrome (sidebar / flashbar / trial banner /
// future surfaces). A page picks one layout via `route.meta.layout`; the
// LAYOUT_CHROME table is the single source of truth for which surfaces a
// given layout renders. Adding a new chrome dimension = add a column to
// every entry; adding a new layout = add a new entry. Boolean fields on
// individual route records are avoided — keep meta a single `layout`
// string so it doesn't drift into a junk drawer.
export type LayoutName = 'default' | 'canvas' | 'notification' | 'minimal';

type Chrome = {
  sidebar: boolean;
  flashbar: boolean;
  trialBanner: boolean;
};

const LAYOUT_CHROME: Record<LayoutName, Chrome> = {
  default: { sidebar: true, flashbar: true, trialBanner: true },
  // Editors that want maximum vertical real estate (SQL editor, notebook).
  canvas: { sidebar: false, flashbar: true, trialBanner: true },
  // The /notification page IS the inbox — suppress the top flashbar so the
  // same unread set doesn't render twice on the screen.
  notification: { sidebar: true, flashbar: false, trialBanner: true },
  // Error pages render their own full-bleed visual; suppress all host chrome
  // so the page isn't framed by an unrelated sidebar / banner.
  minimal: { sidebar: false, flashbar: false, trialBanner: false },
};

export function useChrome(): ComputedRef<Chrome> {
  const route = useRoute();
  return computed(() => LAYOUT_CHROME[(route.meta.layout as LayoutName | undefined) ?? 'default']);
}

// Whether the signed-in user satisfies a route's `meta.perms`. Pure so the
// reactive gate in AppLayout can wrap it in a computed: a route the user lacks
// perms for renders the permission-denied page in place (URL + chrome
// preserved) rather than redirecting, matching legacy's in-place 401 component
// swap. `hasPerm` is injected so this stays free of a setup context.
export function routePermitted(
  meta: RouteLocationNormalized['meta'],
  hasPerm: (key: PermsKey) => boolean,
): boolean {
  const required = meta?.perms;
  if (!required?.length) return true;
  return required.every(hasPerm);
}

declare module 'vue-router' {
  interface RouteMeta {
    renderer?: 'native' | 'legacy';
    authFree?: boolean;
    layout?: LayoutName;
    // Required permission KEYS (PermsKey names, e.g. 'billing@all'); hasPerm
    // translates each to its wire code. An unmet route renders the
    // permission-denied page in place (see routePermitted / AppLayout).
    perms?: PermsKey[];
  }
}
